/**
 * 診療予約データの要通知判定ロジック
 * 
 * 意図：
 * - リスクの高い患者を自動的に検出
 * - 医療安全のための優先度付け
 * - 迅速な対応が必要なケースの特定
 * 
 * 注意点：
 * - BMI計算時の単位に注意（身長はcm、体重はkg）
 * - 診療開始時間との比較は現在時刻を基準
 * - CSVデータの欠損値に対する適切な処理
 */

import type { MedicalReservationRecord, NotificationStatus } from '../types/medicalReservation';

/**
 * BMIを計算する
 * @param height 身長（cm）
 * @param weight 体重（kg）
 * @returns BMI値（計算不可の場合はnull）
 */
const calculateBMI = (height: string | undefined, weight: string | undefined): number | null => {
  if (!height || !weight) return null;
  
  const heightNum = parseFloat(height);
  const weightNum = parseFloat(weight);
  
  if (isNaN(heightNum) || isNaN(weightNum) || heightNum <= 0 || weightNum <= 0) {
    return null;
  }
  
  // BMI = 体重(kg) / (身長(m) * 身長(m))
  const heightInMeters = heightNum / 100;
  return weightNum / (heightInMeters * heightInMeters);
};

/**
 * タバコの本数を取得
 * @param tobacco タバコ情報（例: "14本/日"）
 * @returns 喫煙本数（数値取得できない場合は0）
 */
const getTobaccoCount = (tobacco: string | undefined): number => {
  if (!tobacco) return 0;
  
  // "14本/日" や "14本" などから数値を抽出
  const match = tobacco.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

/**
 * 診療開始時刻まで60分以内かチェック
 * @param consultationStartTime 診療開始時刻（YYYY-MM-DD HH:mm:ss形式）
 * @returns 60分以内の場合true
 */
const isWithin60Minutes = (consultationStartTime: string): boolean => {
  try {
    const consultationTime = new Date(consultationStartTime);
    const now = new Date();
    const diffInMilliseconds = consultationTime.getTime() - now.getTime();
    const diffInMinutes = diffInMilliseconds / (1000 * 60);
    
    return diffInMinutes >= 0 && diffInMinutes <= 60;
  } catch {
    return false;
  }
};

/**
 * 診療予約データが処方不可通知対象かチェック
 * @param record 診療予約レコード
 * @returns 処方不可通知の場合true
 */
export const checkPrescriptionUnavailable = (record: MedicalReservationRecord): boolean => {
  const csvData = record.csvData;
  
  // 必要なデータの取得
  const height = csvData['身長'];
  const weight = csvData['体重'];
  const bmi = calculateBMI(height, weight);
  
  // BMIが35以上の場合は処方不可通知
  if (bmi && bmi >= 35) {
    return true;
  }
  
  return false;
};

/**
 * 処方不可通知の理由を取得
 * @param record 診療予約レコード
 * @returns 処方不可通知の理由リスト
 */
export const getPrescriptionUnavailableReasons = (record: MedicalReservationRecord): string[] => {
  const reasons: string[] = [];
  const csvData = record.csvData;
  
  const height = csvData['身長'];
  const weight = csvData['体重'];
  const bmi = calculateBMI(height, weight);
  
  if (bmi && bmi >= 35) {
    reasons.push(`BMIが35以上（${bmi.toFixed(1)}）のため、処方不可`);
  }
  
  return reasons;
};

/**
 * 通知ステータスを取得
 * @param record 診療予約レコード
 * @returns 通知ステータス
 */
export const getNotificationStatus = (record: MedicalReservationRecord): NotificationStatus => {
  // 優先順位: 処方不可通知 > 要通知 > 空白
  if (checkPrescriptionUnavailable(record)) {
    return 'prescription_unavailable';
  }
  
  if (checkNotificationRequired(record)) {
    return 'notification_required';
  }
  
  return 'none';
};

/**
 * 診療予約データが要通知対象かチェック
 * @param record 診療予約レコード
 * @returns 要通知の場合true
 */
export const checkNotificationRequired = (record: MedicalReservationRecord): boolean => {
  const csvData = record.csvData;
  
  // 必要なデータの取得
  const age = csvData['年齢'] ? parseInt(csvData['年齢'], 10) : 0;
  const height = csvData['身長'];
  const weight = csvData['体重'];
  const bmi = calculateBMI(height, weight);
  const highBloodPressure = csvData['高血圧診断'];
  const tobacco = csvData['タバコ'];
  const tobaccoCount = getTobaccoCount(tobacco);
  const otherDiseases = csvData['その他疾患'] || '';
  const familyThrombosis = csvData['ご家族に血栓症'];
  const migraine = csvData['前兆のある片頭痛'];
  const pregnancyDesire = csvData['妊娠希望'];
  const pregnancyHistory = csvData['妊娠・出産・授乳有無'];
  const memberRegistration = csvData['会員登録'];
  
  // 判定条件のチェック
  
  // 1. 年齢が46歳以上
  if (age >= 46) {
    return true;
  }
  
  // 2. BMIが35以上は処方不可通知に移動したのでスキップ
  // if (bmi && bmi >= 35) {
  //   return true;
  // }
  
  // 3. 年齢が40歳以上かつBMIが30以上
  if (age >= 40 && bmi && bmi >= 30) {
    return true;
  }
  
  // 4. 年齢が40歳以上かつ喫煙が14本以上
  if (age >= 40 && tobaccoCount >= 14) {
    return true;
  }
  
  // 5. 年齢が40歳以上かつ既往に高血圧/糖尿病/脂質異常症
  if (age >= 40) {
    const hasRiskDisease = 
      highBloodPressure === 'あり' ||
      highBloodPressure === '○' ||
      otherDiseases.includes('糖尿病') ||
      otherDiseases.includes('脂質異常症');
    
    if (hasRiskDisease) {
      return true;
    }
  }
  
  // 6. 年齢が40歳以上かつ血栓症の家族歴
  if (age >= 40 && (familyThrombosis === 'あり' || familyThrombosis === '○')) {
    return true;
  }
  
  // 7. 年齢が40歳以上かつ片頭痛
  if (age >= 40 && (migraine === 'あり' || migraine === '○')) {
    return true;
  }
  
  // 8. 授乳中/妊娠中/妊娠希望/出産直後
  const pregnancyRelated = 
    pregnancyDesire === 'あり' ||
    pregnancyDesire === '○' ||
    (pregnancyHistory && (
      pregnancyHistory.includes('授乳中') ||
      pregnancyHistory.includes('妊娠中') ||
      pregnancyHistory.includes('出産直後')
    ));
  
  if (pregnancyRelated) {
    return true;
  }
  
  // 9. 現在時刻が診療開始時間より60分以内かつ会員登録が未回答
  if (isWithin60Minutes(record.consultationStartTime) && 
      (memberRegistration === '未回答' || memberRegistration === '×' || !memberRegistration)) {
    return true;
  }
  
  return false;
};

/**
 * 要通知の理由を取得（デバッグ・表示用）
 * @param record 診療予約レコード
 * @returns 要通知の理由リスト
 */
export const getNotificationReasons = (record: MedicalReservationRecord): string[] => {
  const reasons: string[] = [];
  const csvData = record.csvData;
  
  const age = csvData['年齢'] ? parseInt(csvData['年齢'], 10) : 0;
  const height = csvData['身長'];
  const weight = csvData['体重'];
  const bmi = calculateBMI(height, weight);
  const highBloodPressure = csvData['高血圧診断'];
  const tobacco = csvData['タバコ'];
  const tobaccoCount = getTobaccoCount(tobacco);
  const otherDiseases = csvData['その他疾患'] || '';
  const familyThrombosis = csvData['ご家族に血栓症'];
  const migraine = csvData['前兆のある片頭痛'];
  const pregnancyDesire = csvData['妊娠希望'];
  const pregnancyHistory = csvData['妊娠・出産・授乳有無'];
  const memberRegistration = csvData['会員登録'];
  
  if (age >= 46) {
    reasons.push('年齢が46歳以上');
  }
  
  // BMIが35以上は処方不可通知に移動
  // if (bmi && bmi >= 35) {
  //   reasons.push(`BMIが35以上（${bmi.toFixed(1)}）`);
  // }
  
  if (age >= 40 && bmi && bmi >= 30) {
    reasons.push(`年齢40歳以上かつBMI30以上（${bmi.toFixed(1)}）`);
  }
  
  if (age >= 40 && tobaccoCount >= 14) {
    reasons.push(`年齢40歳以上かつ喫煙${tobaccoCount}本/日`);
  }
  
  if (age >= 40) {
    const riskDiseases = [];
    if (highBloodPressure === 'あり' || highBloodPressure === '○') {
      riskDiseases.push('高血圧');
    }
    if (otherDiseases.includes('糖尿病')) {
      riskDiseases.push('糖尿病');
    }
    if (otherDiseases.includes('脂質異常症')) {
      riskDiseases.push('脂質異常症');
    }
    if (riskDiseases.length > 0) {
      reasons.push(`年齢40歳以上かつ既往歴（${riskDiseases.join('・')}）`);
    }
  }
  
  if (age >= 40 && (familyThrombosis === 'あり' || familyThrombosis === '○')) {
    reasons.push('年齢40歳以上かつ血栓症の家族歴');
  }
  
  if (age >= 40 && (migraine === 'あり' || migraine === '○')) {
    reasons.push('年齢40歳以上かつ片頭痛');
  }
  
  const pregnancyReasons = [];
  if (pregnancyDesire === 'あり' || pregnancyDesire === '○') {
    pregnancyReasons.push('妊娠希望');
  }
  if (pregnancyHistory) {
    if (pregnancyHistory.includes('授乳中')) pregnancyReasons.push('授乳中');
    if (pregnancyHistory.includes('妊娠中')) pregnancyReasons.push('妊娠中');
    if (pregnancyHistory.includes('出産直後')) pregnancyReasons.push('出産直後');
  }
  if (pregnancyReasons.length > 0) {
    reasons.push(pregnancyReasons.join('・'));
  }
  
  if (isWithin60Minutes(record.consultationStartTime) && 
      (memberRegistration === '未回答' || memberRegistration === '×' || !memberRegistration)) {
    reasons.push('診療開始まで60分以内かつ会員登録未回答');
  }
  
  return reasons;
};